<script setup lang="ts">
/**
 * Gauge: a half ring filled up to the value, with an optional target mark, the
 * figure in the middle and the scale ends underneath. The fill's `tone` says
 * how the value stands (`success` target met, `warning` behind) — the caller
 * decides; the gauge does not judge.
 *
 * It keeps its proportions and fits the box it is given (width and height).
 * Exposed as a `meter` with value, range and a text naming the target.
 */
import { computed, inject } from 'vue';
import { i18nInjectionKey } from '../internal';
import { formatCompactNumber } from './axis';
import { arcPath, polar } from './geometry';
import type { NxChartTone } from './series';

const props = withDefaults(
  defineProps<{
    value: number | null | undefined;
    min?: number;
    max: number;
    target?: number | null;
    /** Colour of the fill. Defaults to `brand`. */
    tone?: NxChartTone;
    /** Caption under the figure („Auslastung“). */
    label?: string;
    /** Caption of the target mark; `{value}` is replaced by the formatted target. */
    targetLabel?: string;
    ariaLabel?: string;
    /** Formats the figure. Defaults to a compact number („3,2 Mio.“). */
    formatValue?: (value: number) => string;
    /** Formats the scale ends and the target. Defaults to a compact number. */
    formatScale?: (value: number) => string;
    locale?: string;
  }>(),
  {
    min: 0,
    target: null,
    tone: 'brand',
    label: undefined,
    targetLabel: 'Ziel {value}',
    ariaLabel: undefined,
    formatValue: undefined,
    formatScale: undefined,
    locale: undefined,
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const compact = (v: number) => formatCompactNumber(v, locale.value);
const fmtValue = (v: number) => (props.formatValue ?? compact)(v);
const fmtScale = (v: number) => (props.formatScale ?? compact)(v);

// Geometry in viewBox units: a half ring centred at the bottom.
const W = 200;
const CX = 100;
const CY = 100;
const R = 80;
const STROKE = 16;
const H = CY + STROKE / 2 + 2;

const range = computed(() => {
  const min = Number.isFinite(props.min) ? props.min : 0;
  const max = Number.isFinite(props.max) && props.max > min ? props.max : min + 1;
  return { min, max };
});
function share(v: number): number {
  const { min, max } = range.value;
  return Math.max(0, Math.min(1, (v - min) / (max - min)));
}

const hasValue = computed(() => typeof props.value === 'number' && Number.isFinite(props.value));
const valueShare = computed(() => (hasValue.value ? share(props.value as number) : 0));

const track = arcPath(CX, CY, R, Math.PI, 0);
const fill = computed(() =>
  valueShare.value > 0.001 ? arcPath(CX, CY, R, Math.PI, Math.PI * (1 - valueShare.value)) : ''
);

const hasTarget = computed(() => typeof props.target === 'number' && Number.isFinite(props.target));
const targetMark = computed(() => {
  if (!hasTarget.value) return null;
  const a = Math.PI * (1 - share(props.target as number));
  const inner = polar(CX, CY, R - STROKE / 2 - 5, a);
  const outer = polar(CX, CY, R + STROKE / 2 + 5, a);
  return { x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y };
});
const targetText = computed(() =>
  hasTarget.value ? props.targetLabel.replace('{value}', fmtScale(props.target as number)) : ''
);

const figure = computed(() => (hasValue.value ? fmtValue(props.value as number) : '–'));
const fullFigure = computed(() =>
  hasValue.value ? (props.value as number).toLocaleString(locale.value) : ''
);
const color = computed(() => `var(--nxt-${props.tone}-bg)`);
</script>

<template>
  <div
    class="nx-gauge"
    role="meter"
    :aria-label="props.ariaLabel ?? props.label"
    :aria-valuenow="hasValue ? (props.value as number) : undefined"
    :aria-valuemin="range.min"
    :aria-valuemax="range.max"
    :aria-valuetext="[fullFigure, targetText].filter(Boolean).join(' · ') || undefined"
  >
    <div class="nx-gauge__dial">
      <svg class="nx-gauge__svg" :viewBox="`0 0 ${W} ${H}`" aria-hidden="true">
        <path class="nx-gauge__track" :d="track" :stroke-width="STROKE" />
        <path
          v-if="fill"
          class="nx-gauge__fill"
          :d="fill"
          :stroke-width="STROKE"
          :style="{ stroke: color }"
          pathLength="1"
        />
        <line
          v-if="targetMark"
          class="nx-gauge__target"
          :x1="targetMark.x1"
          :y1="targetMark.y1"
          :x2="targetMark.x2"
          :y2="targetMark.y2"
        />
      </svg>
      <div class="nx-gauge__center" :title="fullFigure">
        <span class="nx-gauge__figure">{{ figure }}</span>
        <span v-if="props.label" class="nx-gauge__label">{{ props.label }}</span>
      </div>
    </div>
    <div class="nx-gauge__scale" aria-hidden="true">
      <span>{{ fmtScale(range.min) }}</span>
      <span v-if="targetText" class="nx-gauge__target-text">{{ targetText }}</span>
      <span>{{ fmtScale(range.max) }}</span>
    </div>
  </div>
</template>

<style scoped>
.nx-gauge {
  container-type: size;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-2xs);
  width: 100%;
  height: 100%;
  min-height: calc(var(--nxt-size-unit) * 7);
}
/* The dial takes the largest 200:108 box that fits above the scale row. */
.nx-gauge__dial {
  position: relative;
  width: min(100cqw, (100cqh - var(--nxt-space-lg) * 1.5) * 1.85);
  aspect-ratio: 200 / 108;
}
.nx-gauge__svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}
.nx-gauge__track,
.nx-gauge__fill {
  fill: none;
  stroke-linecap: round;
}
.nx-gauge__track {
  stroke: var(--nxt-chart-track);
}
.nx-gauge__fill {
  stroke-dasharray: 1;
  animation: nx-gauge-sweep calc(var(--nxt-motion-slow) * 2.5) var(--nxt-ease-out) both;
}
.nx-gauge__target {
  stroke: var(--nxt-fg);
  stroke-width: 3;
  stroke-linecap: round;
}
.nx-gauge__center {
  position: absolute;
  inset-inline: 18%;
  bottom: 4%;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: var(--nxt-line-height-tight);
  text-align: center;
}
.nx-gauge__figure {
  max-width: 100%;
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: clamp(var(--nxt-font-size-lg), 13cqw, calc(var(--nxt-size-unit) * 3.25));
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-gauge__label {
  max-width: 100%;
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-gauge__scale {
  display: flex;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  width: min(100cqw, (100cqh - var(--nxt-space-lg) * 1.5) * 1.85);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
}
.nx-gauge__target-text {
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-medium);
}
@keyframes nx-gauge-sweep {
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-gauge__fill {
    animation: none;
  }
}
@media (forced-colors: active) {
  .nx-gauge__fill {
    stroke: Highlight !important;
  }
}
</style>
